import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

function mmToPt(mm) {
    return (mm * 72) / 25.4;
}

async function loadPdfLib(file) {
    const bytes = await file.arrayBuffer();
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

async function loadPdfJs(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    try {
        return await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }
}

function applyCropBox(page, x, y, width, height) {
    const safeW = Math.max(1, width);
    const safeH = Math.max(1, height);
    page.setCropBox(x, y, safeW, safeH);
    page.setMediaBox(x, y, safeW, safeH);
}

function cropFromMargin(page, marginMm) {
    const marginPt = mmToPt(Math.max(0, marginMm));
    const { width, height } = page.getSize();
    const cropW = width - marginPt * 2;
    const cropH = height - marginPt * 2;
    if (cropW < 36 || cropH < 36) throw new Error('marginTooLarge');
    applyCropBox(page, marginPt, marginPt, cropW, cropH);
}

function cropFromRegion(page, region) {
    const { width: pw, height: ph } = page.getSize();
    const cropW = region.width * pw;
    const cropH = region.height * ph;
    const x = region.left * pw;
    const y = ph - region.top * ph - cropH;
    if (cropW < 36 || cropH < 36) throw new Error('regionTooSmall');
    applyCropBox(page, x, y, cropW, cropH);
}

async function detectContentBounds(page, threshold = 245) {
    const scale = 1.5;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    await page.render({ canvasContext: ctx, viewport, background: '#ffffff' }).promise;

    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pad = Math.round(6 * scale);
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let found = false;

    for (let py = 0; py < height; py++) {
        for (let px = 0; px < width; px++) {
            const i = (py * width + px) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            if (r < threshold || g < threshold || b < threshold) {
                found = true;
                if (px < minX) minX = px;
                if (py < minY) minY = py;
                if (px > maxX) maxX = px;
                if (py > maxY) maxY = py;
            }
        }
    }

    const pageW = viewport.width / scale;
    const pageH = viewport.height / scale;
    if (!found) return { x: 0, y: 0, width: pageW, height: pageH };

    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);

    const cropW = (maxX - minX + 1) / scale;
    const cropH = (maxY - minY + 1) / scale;
    const x = minX / scale;
    const y = pageH - (maxY + 1) / scale;

    return { x, y, width: cropW, height: cropH };
}

/**
 * Render a page as a JPEG data URL for manual crop preview.
 * @param {File} file
 * @param {number} pageNumber
 */
export async function renderPdfPagePreview(file, pageNumber = 1) {
    const pdfDoc = await loadPdfJs(file);
    const page = await pdfDoc.getPage(pageNumber);
    const scale = 1.25;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport, background: '#ffffff' }).promise;
    return canvas.toDataURL('image/jpeg', 0.88);
}

/**
 * Crop pages in a PDF.
 * @param {File} file
 * @param {{ mode: 'margin'|'auto'|'manual', marginMm?: number, region?: { left: number, top: number, width: number, height: number }, regions?: Record<number, { left: number, top: number, width: number, height: number }> }} options
 *   `regions` maps 1-based page numbers to crop regions; pages without an entry are left unchanged.
 * @param {(page: number, total: number) => void} [onProgress]
 */
export async function cropPdf(file, options, onProgress) {
    const pdf = await loadPdfLib(file);
    const pages = pdf.getPages();
    const total = pages.length;

    if (options.mode === 'margin') {
        for (const page of pages) {
            cropFromMargin(page, options.marginMm ?? 0);
        }
    } else if (options.mode === 'manual') {
        if (options.regions && Object.keys(options.regions).length) {
            for (const [pageNumber, region] of Object.entries(options.regions)) {
                const page = pages[Number(pageNumber) - 1];
                if (page && region) cropFromRegion(page, region);
            }
        } else if (options.region) {
            for (const page of pages) {
                cropFromRegion(page, options.region);
            }
        } else {
            throw new Error('noRegion');
        }
    } else {
        const pdfJs = await loadPdfJs(file);
        for (let i = 0; i < total; i++) {
            onProgress?.(i + 1, total);
            const page = await pdfJs.getPage(i + 1);
            const bounds = await detectContentBounds(page);
            applyCropBox(pages[i], bounds.x, bounds.y, bounds.width, bounds.height);
        }
    }

    const output = await pdf.save();
    return new Blob([output], { type: 'application/pdf' });
}
